import React, {useEffect, useState} from "react";
import {Skeleton, Grid, Box, Typography} from "@mui/material";
import {LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from "recharts";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import EmptyIcon from "../../../assets/images/empty-box.png";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";

const VerificationStatusComponent = (props) => {
    const {breakpoints} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {isFetchingVerificationStatusGraph, getVerificationStatusGraph, verificationStatusGraph} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(6, "days").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [formattedVerificationData, setFormattedVerificationData] = useState([]);


    useEffect(() => {
        if (isDashboardResetFilters) {
            setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
        }
    }, [isDashboardResetFilters]);

    useEffect(() => {
        if (startDate && endDate) { 
            getVerificationStatusGraph(startDate, endDate);
            setDashboardResetFilters(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (verificationStatusGraph) {
            const prepareChartData = (data) => {
                const chartData = [];
                const uniqueTimestamps = new Set();
            
                data?.forEach((series) => {
                    series.coords.forEach((point) => uniqueTimestamps.add(point.x));
                });
            
                [...uniqueTimestamps].sort().forEach((timestamp) => {
                    const point = {x: timestamp};

                    data.forEach((series) => {
                        const coord = series.coords.find((c) => c.x === timestamp);
                        point[series.status] = coord ? coord.y : 0;
                    });

                    chartData.push(point);
                });
            
                return chartData;
            }
            
            setFormattedVerificationData(prepareChartData(verificationStatusGraph));
        }
    }, [verificationStatusGraph])

   
    const handleDateRangeChange = (newStartDate, newEndDate) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);   
    }

    const getDateLabelFormat = () => {
        const diffInDays = moment(endDate).diff(moment(startDate), "days");

        if (diffInDays <= 6 || (moment(startDate).isSame(moment(), "month") && moment(endDate).isSame(moment(), "month"))) {
            return "MM/DD"; 

        } else {
            return "MM/DD/YY";
        }
    }

    const tickFormatter = (date) => {
        const format = getDateLabelFormat();

        return moment(date).format(format);
    }

    const formatDate = (date) => moment(date).format("MM/DD/YYYY HH:mm:ss");


    return (
        <Box sx={{backgroundColor: "#fff", mt: "20px", padding: "24px", pt: "24px", borderRadius: "8px"}}>
            <Grid container>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                    <Typography
                        sx={{
                            color: "#58565f",
                            fontSize: 18,
                            mb: 3,
                            fontWeight: 600,
                        }}  
                    >
                        Verification Status
                    </Typography>
                </Grid>

                <Grid item xs={6} sm={6} md={6} lg={6} xl={6} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex"}}>
                    <DateRangePickerComponent
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                            ranges={{
                                "Today": [moment().subtract(1, 'day'), moment()],
                                "Last 7 Days": [moment().subtract(6, "days"), moment()],
                                "This Month": [moment().startOf("month"), moment().endOf("month")],
                                "Last Month": [
                                    moment().subtract(1, "month").startOf("month"),
                                    moment().subtract(1, "month").endOf("month"),
                                ],
                                "Last 90 Days": [moment().subtract(89, "days"), moment()],
                            }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>
           
            {(isFetchingVerificationStatusGraph) ?
                <Box sx={{height: 615}}>
                    <Skeleton variant="rounded" sx={{backgroundColor: "#d8d8fdbd"}} height={"100%"} />
                </Box>
            : 
                (!isFetchingVerificationStatusGraph && verificationStatusGraph?.length === 0) ?
                    <Box sx={{height: 615, textAlign: "center", flexDirection: "column", 
                        justifyContent: "center", display: "flex", alignItems: "center", 
                    }}>
                        <img alt="empty-data-icon" src={EmptyIcon} width="200" style={{marginTop: "-84px"}} />

                        <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                            No data available for the selected date range
                        </Typography>
                    </Box>
            :
                
                <ResponsiveContainer width="100%" height={615} style={{paddingTop: "12px"}}>
                    <LineChart
                        width={1000}
                        height={500}
                        data={formattedVerificationData}
                        margin={{top: 20, right: 30, left: 20, bottom: 20}}
                    >
                        <CartesianGrid strokeDasharray="3 3" />

                        <XAxis
                            dataKey="x"
                            tickFormatter={formatDate}
                            tick={{
                                fill: "#555",
                                fontSize: 12,
                            }}
                            tickLine={{stroke: "#ccc", strokeWidth: 1}}
                        />

                        <YAxis
                            tick={{
                                fontSize: 12,
                                fontWeight: "bold",
                                fill: "#555",
                            }}
                        />

                        <Tooltip
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label) => formatDate(label)}
                        />

                        <Legend />

                        {verificationStatusGraph?.map((series) => (
                            <Line
                                key={series.status}
                                type="monotone"
                                dataKey={series.status}
                                stroke={
                                    series.status === "Successful"? "#40BF7E"
                                : 
                                    series.status === "Suspicious" ? "#FFA500"
                                : 
                                    "#E76C59"
                                }
                                strokeWidth={2} 
                                name={series.status}
                                dot={{
                                    r: 5, 
                                    fill:
                                        series.status === "Successful" ? "#40BF7E"
                                    : 
                                        series.status === "Suspicious" ? "#FFA500"
                                    : 
                                        "#E76C59",
                                    stroke: "#fff", 
                                    strokeWidth: 1.5,
                                }}
                                activeDot={{
                                    r: 6,
                                    fill: "#555",
                                    stroke: "#40BF7E",
                                    strokeWidth: 2,
                                }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            }
        </Box>
    )
}

export default VerificationStatusComponent